import axios from 'axios';
import { clearSessionStorage, broadcastLogout } from './useSessionManager';

const API_URL = import.meta.env.VITE_API_URL;

// Instance axios principale
const axiosInstance = axios;

// Instance axios dédiée exclusivement au rafraîchissement des tokens (sans intercepteurs imbriqués)
const refreshClient = axios.create({
    baseURL: API_URL
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const isRefreshRequest = (config = {}) => {
    const url = config.url || '';
    return url.includes('/auth/refresh-token');
};

const shouldTryRefresh = (error, originalRequest) => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!token || !refreshToken) {
        return false;
    }

    const status = error && error.response ? error.response.status : undefined;
    const data = error && error.response ? error.response.data : {};
    const message = String(data.message || '').toLowerCase();
    const code = data.code;

    if (code === 'SESSION_EXPIRED') {
        return false;
    }

    const unauthorized = status === 401;
    const tokenForbidden = status === 403 && (message.includes('token') || message.includes('session'));

    return (unauthorized || tokenForbidden) &&
        !originalRequest._retry &&
        !originalRequest.skipAuthRefresh &&
        !isRefreshRequest(originalRequest);
};

// Intercepteur de requête : injection transparente du token JWT Bearer
axiosInstance.interceptors.request.use(
    (config) => {
        if (isRefreshRequest(config) || config.url?.includes('/auth/login') || config.url?.includes('/auth/register')) {
            return config;
        }

        const token = localStorage.getItem('token');
        if (token) {
            config.headers = config.headers || {};
            if (!config.headers.Authorization) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Intercepteur de réponse : rafraîchissement transparent du token & gestion stricte des 401
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const code = error.response?.data?.code;

        // Si le backend signale explicitement que la session est invalide ou déconnectée par l'admin
        if (code === 'SESSION_EXPIRED' || code === 'GLOBAL_SESSION_FLUSHED' || code === 'SESSION_INVALIDATED') {
            if (!window.location.pathname.includes('/login')) {
                clearSessionStorage();
                sessionStorage.setItem('session_expired_alert', 'true');
                sessionStorage.setItem('session_expired_message', error.response?.data?.message || 'Votre session a été réinitialisée. Veuillez vous reconnecter.');
                broadcastLogout('SESSION_EXPIRED');
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // Tenter le rafraîchissement automatique de token si 401 / 403
        if (originalRequest && shouldTryRefresh(error, originalRequest)) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                .then((token) => {
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                })
                .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (!refreshToken) {
                    throw { isAuthError: true, message: 'Refresh token manquant' };
                }

                // Appel sécurisé au service de rafraîchissement
                const response = await refreshClient.post('/auth/refresh-token', { refreshToken });
                const { token: newToken, refreshToken: newRefreshToken } = response.data;

                if (!newToken) {
                    throw { isAuthError: true, message: 'Réponse refresh invalide' };
                }

                // Sauvegarde des nouveaux jetons
                localStorage.setItem('token', newToken);
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', newRefreshToken);
                }

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                processQueue(null, newToken);
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);

                // DÉCISION IMPORTANTE : Déconnecter UNIQUEMENT si le serveur retourne un statut HTTP 401 ou 403 explicite
                const isExplicitAuthFailure = refreshError.isAuthError ||
                    (refreshError.response && (refreshError.response.status === 401 || refreshError.response.status === 403));

                if (isExplicitAuthFailure) {
                    if (!window.location.pathname.includes('/login')) {
                        clearSessionStorage();
                        sessionStorage.setItem('session_expired_alert', 'true');
                        sessionStorage.setItem('session_expired_message', refreshError.response?.data?.message || 'Votre session a expiré. Veuillez vous reconnecter.');
                        broadcastLogout('REFRESH_FAILED');
                        window.location.href = '/login';
                    }
                } else {
                    // Erreur réseau temporaire ou serveur (500, 502, 503, etc.) : NE PAS DÉCONNECTER
                    console.warn('[axiosConfig] Erreur réseau/serveur lors du refresh-token. La session est maintenue.', refreshError.message || refreshError);
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;